import { ConfirmModal } from "@components/ConfirmModal";
import { CheckIcon } from "@components/Icons/Check";
import { DeleteIcon } from "@components/Icons/Delete";
import { EditIcon } from "@components/Icons/Edit";
import { EyeIcon } from "@components/Icons/EyeIcon";
import { LargeGridIcon } from "@components/Icons/LargeGrid";
import { PlayMediaIcon } from "@components/Icons/PlayMedia";
import { SelectedIcon } from "@components/Icons/Selected";
import { SmallGridIcon } from "@components/Icons/SmallGrid";
import { UnselectedIcon } from "@components/Icons/Unselected";
import { MobilePhotoDownloadModal } from "@components/MobilePhotoDownloadModal";
import { OverlaySpinner } from "@components/Loading/OverlaySpinner";
import { FirebaseContext } from "@helpers/firebase";
import { getMediaUrl } from "@helpers/getMediaUrl";
import { useIsMobile } from "@helpers/useIsMobile";
import { useToast } from "@helpers/useToast";
import { useTranslation } from "@helpers/useTranslation";
import { getExtraChallengeId, isLegacyExtra } from "@helpers/extrasMapHelper";
import { deleteDoc, deleteField, doc, updateDoc } from "firebase/firestore";
import lgThumbnail from "lightgallery/plugins/thumbnail";
import lgVideo from "lightgallery/plugins/video";
import lgZoom from "lightgallery/plugins/zoom";
import LightGallery from "lightgallery/react";
import { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import "./LightGallery.css";
import { PLACEHOLDER_IMAGE } from "./PlaceholderImage";

const isMediaChallenge = (challenge: Challenge) =>
  !challenge.hidden && (challenge.type === "photo" || challenge.type === "video" || challenge.type === "photovideo");

type Props = {
  challenges: Challenge[];
  teams: Team[];
  eventId: string;
  allowDownload: boolean;
  allowDelete?: boolean;
  downloadPassword?: string;
};

export const Pictures: FC<Props> = ({ challenges, teams, eventId, allowDownload, allowDelete, downloadPassword }) => {
  const [teamsSelected, setTeamsSelected] = useState<Team[]>(teams);
  const [challengesSelected, setChallengesSelected] = useState<Challenge[]>(() => challenges.filter(isMediaChallenge));
  const [teamsExpanded, setTeamsExpanded] = useState(false);
  const [challengesExpanded, setChallengesExpanded] = useState(false);
  const { t } = useTranslation("admin");
  const [downloading, setDownloading] = useState(false);
  const lightGalleryRef = useRef(null);
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});
  const [isDenseGrid, setIsDenseGrid] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentImageUri, setCurrentImageUri] = useState<string | null>(null);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [isLgContainerVisible, setIsLgContainerVisible] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [showMobileDownloadModal, setShowMobileDownloadModal] = useState(false);
  const [mobileDownloadImageUri, setMobileDownloadImageUri] = useState<string | null>(null);
  const [mobileDownloadTitle, setMobileDownloadTitle] = useState<string>("");
  const [savedGalleryIndex, setSavedGalleryIndex] = useState<number>(0);
  const { storage } = useContext(FirebaseContext);
  const isMobile = useIsMobile();

  // New pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(40); // Default items per page

  // Constants for when to show collapsed view
  const TEAMS_COLLAPSE_THRESHOLD = 4;
  const CHALLENGES_COLLAPSE_THRESHOLD = 4;

  // Initialize expanded state based on thresholds
  useEffect(() => {
    setTeamsExpanded(teams.length <= TEAMS_COLLAPSE_THRESHOLD);
    const photoVideoChallenges = challenges.filter(isMediaChallenge);
    setChallengesExpanded(photoVideoChallenges.length <= CHALLENGES_COLLAPSE_THRESHOLD);
  }, [teams.length, challenges.length]);

  // Clean up LightGallery instance on unmount
  useEffect(() => {
    return () => {
      try {
        if (lightGalleryRef.current) {
          const lgInstance = lightGalleryRef.current;
          // Ensure we're not already destroyed
          if (lgInstance.el && document.body.contains(lgInstance.el)) {
            lgInstance.destroy();
          }
        }
      } catch (error) {
        console.warn("Error cleaning up LightGallery:", error);
      }
    };
  }, []);

  const { firestore } = useContext(FirebaseContext);

  const handleDelete = async (uri: string) => {
    // uri comes in the shape of:
    // "events/${eventId}/teams/${team.id}/solutions/${solutionId}" for solutions
    // "events/${eventId}/teams/${team.id}/extras/${extraId}" for extras
    const components = uri.split("/");
    const isSolution = components[4] === "solutions";
    const isExtra = components[4] === "extras";
    const teamId = components[3];
    const imageId = components[components.length - 1];
    const cleanImageId = imageId.replace(/_light$/, "");

    // Check if this is a video by looking for -video suffix
    const isVideo = cleanImageId.endsWith("-video");
    const baseId = isVideo ? cleanImageId.replace("-video", "") : cleanImageId;

    try {
      if (isSolution) {
        // Remove from map field
        const teamDocRef = doc(firestore, `events/${eventId}/teams/${teamId}`);
        await updateDoc(teamDocRef, {
          [`solutionsMap.${baseId}`]: deleteField(),
          // Also remove the video entry if it exists
          [`solutionsMap.${baseId}-video`]: deleteField(),
        });

        // Remove from collection (future proofing)
        const docRef = doc(firestore, `events/${eventId}/teams/${teamId}/solutions/${baseId}`);
        await deleteDoc(docRef);

        // Also remove video document if it exists
        const videoDocRef = doc(firestore, `events/${eventId}/teams/${teamId}/solutions/${baseId}-video`);
        await deleteDoc(videoDocRef);
      }

      if (isExtra) {
        // Remove from map field
        const teamDocRef = doc(firestore, `events/${eventId}/teams/${teamId}`);
        await updateDoc(teamDocRef, {
          [`extrasMap.${baseId}`]: deleteField(),
          // Also remove the video entry if it exists
          [`extrasMap.${baseId}-video`]: deleteField(),
        });

        // Remove from collection (future proofing)
        const docRef = doc(firestore, `events/${eventId}/teams/${teamId}/extras/${baseId}`);
        await deleteDoc(docRef);

        // Also remove video document if it exists
        const videoDocRef = doc(firestore, `events/${eventId}/teams/${teamId}/extras/${baseId}-video`);
        await deleteDoc(videoDocRef);
      }
    } catch (error) {
      console.error(error);
    }

    toast(t("deleted"), "✏️", "success", 1000);
  };

  const handleImageError = useCallback((imageUri: string) => {
    setLoadedImages((prev) => ({
      ...prev,
      [imageUri]: false,
    }));
  }, []);

  const handleImageLoad = useCallback((imageUri: string) => {
    setLoadedImages((prev) => ({
      ...prev,
      [imageUri]: true,
    }));
  }, []);

  const toggleTeam = (team: Team) => {
    if (allTeamsSelected()) {
      setTeamsSelected([team]);
      // Expand when selecting individual team
      setTeamsExpanded(true);
      return;
    }

    if (teamsSelected.includes(team)) {
      const newTeams = teamsSelected.filter((t) => t.id !== team.id);
      setTeamsSelected(newTeams);
    } else {
      const newTeams = [team, ...teamsSelected];
      setTeamsSelected(newTeams);
    }
  };

  const isTeamSelected = (team: Team) => teamsSelected.find((t) => t.id === team.id);
  const allTeamsSelected = () => teamsSelected.length === teams.length;

  const toggleAllTeams = () => {
    if (allTeamsSelected()) {
      setTeamsSelected([]);
    } else {
      setTeamsSelected(teams);
    }
    // Collapse when selecting all if above threshold
    if (!allTeamsSelected() && teams.length > TEAMS_COLLAPSE_THRESHOLD) {
      setTeamsExpanded(false);
    }
  };

  const toggleChallenge = (challenge: Challenge) => {
    if (allChallengesSelected()) {
      setChallengesSelected([challenge]);
      return;
    }

    if (challengesSelected.includes(challenge)) {
      const newChallenges = challengesSelected.filter((c) => c.id !== challenge.id);
      setChallengesSelected(newChallenges);
    } else {
      const newChallenges = [challenge, ...challengesSelected];
      setChallengesSelected(newChallenges);
    }
  };

  const isChallengeSelected = (challenge: Challenge) => {
    return challengesSelected.find((c) => c.id === challenge.id);
  };

  const allChallengesSelected = () => {
    const photoVideoChallenges = challenges.filter(isMediaChallenge);
    return challengesSelected.length === photoVideoChallenges.length;
  };

  const toggleAllChallenges = () => {
    const photoVideoChallenges = challenges.filter(isMediaChallenge);
    if (allChallengesSelected()) {
      setChallengesSelected([]);
    } else {
      setChallengesSelected(photoVideoChallenges);
    }
  };

  const toggleTeamsExpanded = () => {
    setTeamsExpanded(!teamsExpanded);
  };

  const toggleChallengesExpanded = () => {
    setChallengesExpanded(!challengesExpanded);
  };

  const toast = useToast();
  const { functions } = useContext(FirebaseContext);
  const [downloadPhoto] = useHttpsCallable(functions, "downloadPhotoV2");
  const download = async ({ uri, filename }) => {
    if (!allowDownload) return;
    setDownloading(true);
    try {
      const result = await downloadPhoto({ uri, filename, eventId, password: downloadPassword });
      const url = result?.data as string;
      if (!url) throw new Error("No download URL returned");
      // The signed URL carries Content-Disposition: attachment, so navigating to it
      // triggers a native browser download without leaving the page. This works on
      // mobile where blob/data-URI downloads silently fail for large files.
      window.location.assign(url);
      toast(t("downloadStarted"), "📥", "success");
    } catch (error) {
      console.error(error);
      toast(t("errorDownloading"), "🤷‍♂️", "error");
    } finally {
      setDownloading(false);
    }
  };

  const handleMobileDownload = (imageUri: string, title: string) => {
    setMobileDownloadImageUri(imageUri);
    setMobileDownloadTitle(title);
    setShowMobileDownloadModal(true);
  };

  const closeMobileDownloadModal = () => {
    setShowMobileDownloadModal(false);
    setMobileDownloadImageUri(null);
    setMobileDownloadTitle("");

    // Reopen gallery at the same slide
    setTimeout(() => {
      setIsLgContainerVisible(true);
      // Next tick to ensure the container is visible before opening
      setTimeout(() => {
        if (lightGalleryRef.current) {
          lightGalleryRef.current.openGallery(savedGalleryIndex);
        }
      }, 50);
    }, 100);
  };

  const getAllGalleryItems = useCallback(() => {
    return teams.reduce<JSX.Element[]>((items, team) => {
      if (!isTeamSelected(team)) return items;

      const solutions = Object.keys(team.solutionsMap || {});
      const extras = Object.keys(team.extrasMap || {});

      const solutionItems = solutions
        .filter((solution) => !solution.endsWith("-video"))
        .filter((solution) => {
          // Filter by selected challenges
          const challenge = challenges.find((c) => c.id === solution);
          return challenge ? isChallengeSelected(challenge) : false;
        })
        .map((solution) => {
          const hasVideo = solutions.includes(`${solution}-video`);

          const imageUri = `events/${eventId}/teams/${team.id}/solutions/${solution}_light`;
          const videoUri = hasVideo ? `events/${eventId}/teams/${team.id}/solutions/${solution}.mp4` : null;

          const imageUrl = getMediaUrl(storage, imageUri);
          const videoUrl = videoUri ? getMediaUrl(storage, videoUri) : null;

          const challenge = challenges.find((c) => c.id === solution)?.title || "";
          const title = `${team.displayName ?? team.name}${challenge ? ` - ${challenge}` : ""}`;

          const hdImageUri = `events/${eventId}/teams/${team.id}/solutions/${solution}_heavy`;

          // Skip this item only if it has permanently failed (false), not if it's in retry state (number)
          if (loadedImages[imageUri] === false) return null;

          if (hasVideo) {
            return (
              <a
                key={imageUri}
                data-hd-uri={videoUri}
                data-name={solution}
                data-type="video"
                data-lg-size="1280-720"
                className="gallery-item flex aspect-square w-full items-center justify-center"
                data-video={JSON.stringify({
                  source: [
                    { src: videoUrl, type: "video/mp4" },
                    ...(videoUrl ? [{ src: videoUrl.replace(".mp4", ".webm"), type: "video/webm" }] : []),
                  ],
                  attributes: {
                    preload: "metadata",
                    playsinline: true,
                    controls: true,
                    muted: true,
                  },
                })}
                data-poster={imageUrl || PLACEHOLDER_IMAGE}
                data-sub-html={`<h4>${title}</h4>`}
              >
                <div className="relative h-full w-full">
                  <img
                    src={imageUrl || PLACEHOLDER_IMAGE}
                    alt={title}
                    className={`h-full w-full rounded-lg object-cover ${
                      isDenseGrid ? "aspect-square max-h-[16rem]" : "aspect-video max-h-[30rem]"
                    }`}
                    onError={() => handleImageError(imageUri)}
                    onLoad={() => handleImageLoad(imageUri)}
                    onContextMenu={(e) => e.preventDefault()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                  />
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <PlayMediaIcon className="h-12 w-12 text-white opacity-90 drop-shadow-lg" />
                  </div>
                </div>
                <div className="relative -mt-10 flex h-10 w-full flex-col items-center justify-center bg-gray-100 opacity-90">
                  <div className="text-sm font-semibold">
                    {team.displayName ?? team.name}
                    {hasVideo ? " 🍿🎞️" : null}
                  </div>
                  {challenge && <div className="overflow-hidden text-sm font-light">{challenge}</div>}
                </div>
              </a>
            );
          }

          return (
            <a
              key={imageUri}
              data-lg-size="1280-720"
              className="gallery-item flex aspect-square w-full items-center justify-center"
              data-src={imageUrl || PLACEHOLDER_IMAGE}
              data-hd-uri={hdImageUri}
              data-name={solution}
              data-type="image"
              data-sub-html={`<h4>${title}</h4>`}
              data-video={JSON.stringify({
                source: [
                  { src: videoUrl, type: "video/mp4" },
                  ...(videoUrl ? [{ src: videoUrl.replace(".mp4", ".webm"), type: "video/webm" }] : []),
                ],
                attributes: {
                  preload: "auto",
                  playsinline: true,
                  controls: true,
                  muted: true,
                },
              })}
            >
              <div className="relative h-full w-full">
                <img
                  src={imageUrl || PLACEHOLDER_IMAGE}
                  alt={title}
                  className={`h-full w-full rounded-lg object-cover ${
                    isDenseGrid ? "aspect-square max-h-[16rem]" : "aspect-video max-h-[30rem]"
                  }`}
                  onError={() => handleImageError(imageUri)}
                  onLoad={() => handleImageLoad(imageUri)}
                  onContextMenu={(e) => e.preventDefault()}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                />
              </div>
              <div className="relative -mt-10 flex h-10 w-full flex-col items-center justify-center bg-gray-100 opacity-90">
                <div className="text-sm font-semibold">{team.displayName ?? team.name}</div>
                {challenge && <div className="overflow-hidden text-sm font-light">{challenge}</div>}
              </div>
              {isEditMode && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteClick(imageUri);
                  }}
                  className="absolute top-2 right-2 rounded-full bg-red-500 p-2 text-white transition-opacity duration-200 hover:bg-red-600"
                  title={t("delete")}
                >
                  <DeleteIcon className="h-5 w-5" />
                </button>
              )}
            </a>
          );
        })
        .filter(Boolean); // Remove null items

      // Handle extras: both legacy (no challenge link) and challenge-linked
      let extraItems: JSX.Element[] = [];
      extraItems = extras
        .filter((extra) => !extra.endsWith("-video"))
        .filter((extra) => {
          const extraEntry = team.extrasMap[extra];
          if (!extraEntry) return false;

          // Check if this is a legacy extra or challenge-linked
          if (isLegacyExtra(extraEntry)) {
            // Legacy extras: only show when all challenges are selected
            return allChallengesSelected();
          }

          // Challenge-linked extra: show if its challenge is selected
          const linkedChallengeId = getExtraChallengeId(extraEntry);
          if (!linkedChallengeId) {
            return allChallengesSelected();
          }

          const linkedChallenge = challenges.find((c) => c.id === linkedChallengeId);
          return linkedChallenge ? isChallengeSelected(linkedChallenge) : false;
        })
        .map((extra) => {
          const hasVideo = extras.includes(`${extra}-video`);

          const imageUri = `events/${eventId}/teams/${team.id}/extras/${extra}_light`;
          const videoUri = hasVideo ? `events/${eventId}/teams/${team.id}/extras/${extra}.mp4` : null;

          const imageUrl = getMediaUrl(storage, imageUri);
          const videoUrl = videoUri ? getMediaUrl(storage, videoUri) : null;

          // Get challenge name for challenge-linked extras
          const extraEntry = team.extrasMap[extra];
          const linkedChallengeId = extraEntry ? getExtraChallengeId(extraEntry) : undefined;
          const linkedChallenge = linkedChallengeId ? challenges.find((c) => c.id === linkedChallengeId) : undefined;
          const challengeTitle = linkedChallenge?.title || "";
          const title = `${team.displayName ?? team.name}${challengeTitle ? ` - ${challengeTitle}` : ""}`;

          const hdImageUri = `events/${eventId}/teams/${team.id}/extras/${extra}_heavy`;

          // Skip this item only if it has permanently failed (false), not if it's in retry state (number)
          if (loadedImages[imageUri] === false) return null;

          if (hasVideo) {
            return (
              <a
                key={imageUri}
                data-hd-uri={videoUri}
                data-name={extra}
                data-type="video"
                data-lg-size="1280-720"
                className="gallery-item flex aspect-square w-full items-center justify-center"
                data-video={JSON.stringify({
                  source: [
                    { src: videoUrl, type: "video/mp4" },
                    ...(videoUrl ? [{ src: videoUrl.replace(".mp4", ".webm"), type: "video/webm" }] : []),
                  ],
                  attributes: {
                    preload: "metadata",
                    playsinline: true,
                    controls: true,
                    muted: true,
                  },
                })}
                data-poster={imageUrl || PLACEHOLDER_IMAGE}
                data-sub-html={`<h4>${title}</h4>`}
              >
                <div className="relative h-full w-full">
                  <img
                    src={imageUrl || PLACEHOLDER_IMAGE}
                    alt={title}
                    className={`h-full w-full rounded-lg object-cover ${
                      isDenseGrid ? "aspect-square max-h-[16rem]" : "aspect-video max-h-[30rem]"
                    }`}
                    onError={() => handleImageError(imageUri)}
                    onLoad={() => handleImageLoad(imageUri)}
                    onContextMenu={(e) => e.preventDefault()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                  />
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <PlayMediaIcon className="h-12 w-12 text-white opacity-90 drop-shadow-lg" />
                  </div>
                </div>
                <div className="relative -mt-10 flex h-10 w-full flex-col items-center justify-center bg-gray-100 opacity-90">
                  <div className="text-sm font-semibold">
                    {team.displayName ?? team.name}
                    {hasVideo ? " 🍿🎞️" : null}
                  </div>
                  {challengeTitle && <div className="overflow-hidden text-sm font-light">{challengeTitle}</div>}
                </div>
                {isEditMode && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteClick(imageUri);
                    }}
                    className="absolute top-2 right-2 rounded-full bg-red-500 p-2 text-white transition-opacity duration-200 hover:bg-red-600"
                    title={t("delete")}
                  >
                    <DeleteIcon className="h-5 w-5" />
                  </button>
                )}
              </a>
            );
          }

          return (
            <a
              key={imageUri}
              data-hd-uri={hdImageUri}
              data-type="image"
              data-name={extra}
              className="gallery-item flex aspect-square w-full items-center justify-center"
              data-src={imageUrl || PLACEHOLDER_IMAGE}
              data-sub-html={`<h4>${title}</h4>`}
              data-video={JSON.stringify({
                source: [{ src: videoUrl, type: "video/mp4" }],
                attributes: { preload: "metadata", controls: true },
              })}
            >
              <div className="relative h-full w-full">
                <img
                  src={imageUrl || PLACEHOLDER_IMAGE}
                  alt={title}
                  className={`h-full w-full rounded-lg object-cover ${
                    isDenseGrid ? "aspect-square max-h-[16rem]" : "aspect-video max-h-[30rem]"
                  }`}
                  onError={() => handleImageError(imageUri)}
                  onLoad={() => handleImageLoad(imageUri)}
                  onContextMenu={(e) => e.preventDefault()}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                />
              </div>
              <div className="relative -mt-10 flex h-10 w-full flex-col items-center justify-center bg-gray-100 opacity-90">
                <div className="text-sm font-semibold">{team.displayName ?? team.name}</div>
                {challengeTitle && <div className="overflow-hidden text-sm font-light">{challengeTitle}</div>}
              </div>
              {isEditMode && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteClick(imageUri);
                  }}
                  className="absolute top-2 right-2 rounded-full bg-red-500 p-2 text-white transition-opacity duration-200 hover:bg-red-600"
                  title={t("delete")}
                >
                  <DeleteIcon className="h-5 w-5" />
                </button>
              )}
            </a>
          );
        })
        .filter(Boolean); // Remove null items

      return [...items, ...solutionItems, ...extraItems];
    }, []);
  }, [
    teamsSelected,
    challengesSelected,
    teams,
    challenges,
    eventId,
    loadedImages,
    handleImageError,
    handleImageLoad,
    selectedImages,
  ]);

  const onInit = useCallback((detail) => {
    lightGalleryRef.current = detail.instance;

    const lg = detail.instance;

    // Handle video errors to prevent AbortError from bubbling up
    lg.LGel.on("lgBeforeSlide", () => {
      // Pause any currently playing videos before sliding to prevent AbortError
      const videos = document.querySelectorAll(".lg-item video");
      videos.forEach((video: HTMLVideoElement) => {
        if (!video.paused) {
          video.pause();
        }
      });
    });

    // Wait until the gallery is fully initialized
    lg.LGel.on("lgAfterSlide", () => {
      const downloadBtn = document.querySelector(".lg-download");

      if (downloadBtn) {
        // Add mobile-friendly classes to the download button
        downloadBtn.classList.add("lg-show-mobile");

        // @ts-ignore
        downloadBtn.onclick = async (e) => {
          e.preventDefault();
          e.stopPropagation();

          // build image URI
          const currentSlide = lg.items[lg.index];
          const mediaUri = currentSlide?.getAttribute("data-hd-uri");
          const mediaName = currentSlide?.getAttribute("data-name");
          const mediaType = currentSlide?.getAttribute("data-type");
          const slideTitle = currentSlide?.getAttribute("data-sub-html")?.replace(/<\/?h4>/g, "") || "";

          const extension = mediaType === "video" ? "mp4" : "jpg";
          const filename = `${eventId}-${mediaName}.${extension}`;

          // Store current index before downloading
          const currentIndex = lg.index;

          // On mobile, for photos, show the mobile download modal
          if (isMobile && mediaType === "image") {
            // Save the current gallery index and close gallery temporarily
            setSavedGalleryIndex(currentIndex);
            setIsLgContainerVisible(false);

            // Show mobile download modal
            handleMobileDownload(mediaUri, slideTitle);
            return;
          }

          // Desktop or video: use regular download
          // Close gallery temporarily to prevent UI issues during download
          setIsLgContainerVisible(false);
          await download({ uri: mediaUri, filename });

          // Reopen gallery at the same slide
          setTimeout(() => {
            setIsLgContainerVisible(true);
            // Next tick to ensure the container is visible before opening
            setTimeout(() => {
              if (lightGalleryRef.current) {
                lightGalleryRef.current.openGallery(currentIndex);
              }
            }, 50);
          }, 100);
        };
      }
    });
  }, []);

  const handleDeleteClick = (imageUri: string) => {
    if (isEditMode && selectedImages.size > 1) {
      // If we're in edit mode and have multiple selections, prepare for bulk delete
      setBulkDeleteMode(true);
    } else {
      // Single image delete mode
      setBulkDeleteMode(false);
      setCurrentImageUri(imageUri);
    }
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (bulkDeleteMode) {
      // Delete all selected images
      for (const imageUri of Array.from(selectedImages)) {
        await handleDelete(imageUri);
      }
      // Clear selection after deletion
      clearImageSelection();
    } else if (currentImageUri) {
      // Delete single image
      await handleDelete(currentImageUri);
    }

    setShowConfirmModal(false);
    setCurrentImageUri(null);
    setBulkDeleteMode(false);
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setCurrentImageUri(null);
    setBulkDeleteMode(false);
  };

  const toggleImageSelection = (imageUri: string) => {
    setSelectedImages((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(imageUri)) {
        newSelection.delete(imageUri);
      } else {
        newSelection.add(imageUri);
      }
      return newSelection;
    });
  };

  const isImageSelected = (imageUri: string) => {
    return selectedImages.has(imageUri);
  };

  const clearImageSelection = () => {
    setSelectedImages(new Set());
  };

  // Clear selection when exiting edit mode
  useEffect(() => {
    if (!isEditMode) {
      clearImageSelection();
    }
  }, [isEditMode]);

  // Get all items first, then paginate
  const allGalleryItems = getAllGalleryItems();

  // Calculate total pages
  const totalPages = Math.ceil(allGalleryItems.length / itemsPerPage);

  // Get current items for pagination
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return allGalleryItems.slice(startIndex, endIndex);
  };

  // Get items for current page
  const galleryItems = getCurrentPageItems();

  // Handle pagination navigation
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Function to render pagination controls
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="mt-8 mb-16 flex flex-wrap items-center justify-center gap-2 px-2">
        <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="btn-sm btn">
          {t("previous")}
        </button>

        <div className="flex flex-wrap justify-center gap-1">
          {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
            // Simplified logic for mobile
            let pageNum = currentPage;
            if (totalPages <= 3) {
              pageNum = i + 1;
            } else if (currentPage <= 2) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 1) {
              pageNum = totalPages - 2 + i;
            } else {
              pageNum = currentPage - 1 + i;
            }

            return (
              <button
                key={i}
                onClick={() => goToPage(pageNum)}
                className={`btn-sm btn min-w-[2.5rem] ${pageNum === currentPage ? "btn-primary" : "btn-ghost"}`}
              >
                {pageNum}
              </button>
            );
          })}

          {totalPages > 3 && currentPage < totalPages - 1 && (
            <>
              {currentPage < totalPages - 2 && <span className="self-end pb-2">...</span>}
              <button onClick={() => goToPage(totalPages)} className="btn-ghost btn-sm btn min-w-[2.5rem]">
                {totalPages}
              </button>
            </>
          )}
        </div>

        <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className="btn-sm btn">
          {t("next")}
        </button>

        <div className="w-full sm:w-auto sm:pl-4">
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="select-bordered select select-sm w-full sm:w-auto"
          >
            <option value="20">20</option>
            <option value="40">40</option>
            <option value="100">100</option>
            <option value="200">200</option>
          </select>
        </div>
      </div>
    );
  };

  // Add grid classes based on density state
  const gridClasses = isDenseGrid
    ? "grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8"
    : "grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-12 max-w-[2560px] mx-auto place-items-center";

  const renderDeleteSelectedButton = () => {
    if (allowDelete && isEditMode && selectedImages.size > 0) {
      return (
        <div className="fixed bottom-6 right-6 z-10 flex flex-col space-y-2">
          <button
            onClick={clearImageSelection}
            className="flex items-center rounded-full bg-gray-500 px-4 py-3 text-white shadow-lg transition-all duration-200 hover:bg-gray-600"
          >
            {t("clearSelection")}
          </button>
          <button
            onClick={() => handleDeleteClick("")} // Empty string triggers bulk delete mode
            className="flex items-center rounded-full bg-red-500 px-4 py-3 text-white shadow-lg transition-all duration-200 hover:bg-red-600"
          >
            <DeleteIcon className="mr-2 h-5 w-5" />
            {`${t("deleteSelected")} (${selectedImages.size})`}
          </button>
        </div>
      );
    }
    return null;
  };

  const multiTeam = teams.length > 1;
  const multiChallenge = challenges.length > 1;
  const hasChallenges = challenges.length > 0;

  const teamsToShow = teamsExpanded ? teams : teams.slice(0, TEAMS_COLLAPSE_THRESHOLD);

  return (
    <>
      <OverlaySpinner loading={downloading} className="bg-opacity-80" />
      <div>
        <div className="flex flex-col items-center justify-center">
          {multiTeam && (
            <div className="flex flex-col items-center justify-center pt-6">
              <h3 className="text-sm font-semibold uppercase text-neutral">{t("filterTeams")}</h3>
              <div className="flex flex-wrap items-center justify-center">
                <span
                  onClick={toggleAllTeams}
                  className={`mx-1 mt-2 inline-flex items-center rounded-full bg-gray-50 px-3 py-1 text-sm font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 ${
                    allTeamsSelected() ? "bg-base-200 ring-base-300" : "bg-base-100 px-6 font-light text-opacity-40"
                  } cursor-pointer`}
                >
                  {t("allTeams")}
                  {allTeamsSelected() && <CheckIcon className={`ml-2 h-3 w-3`} />}
                </span>

                {teamsToShow.map((team) => (
                  <span
                    key={team.id}
                    onClick={() => toggleTeam(team)}
                    className={`mx-1 mt-2 inline-flex items-center rounded-full bg-gray-50 px-3 py-1 text-sm font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 ${
                      isTeamSelected(team) ? "bg-base-200 ring-base-300" : "bg-base-100 px-6 text-opacity-40"
                    } cursor-pointer`}
                  >
                    <span>{team.displayName ?? team.name}</span>
                    {isTeamSelected(team) && <CheckIcon className={`ml-2 h-3 w-3`} />}
                  </span>
                ))}

                {teams.length > TEAMS_COLLAPSE_THRESHOLD && (
                  <button
                    onClick={toggleTeamsExpanded}
                    className="mx-1 mt-2 inline-flex items-center px-3 py-1 text-xs font-medium underline"
                    title={teamsExpanded ? t("showLess") : t("showMore")}
                  >
                    {teamsExpanded ? `${t("showLess")}` : `${t("showMore")}`}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {multiChallenge && hasChallenges && (
          <div className="flex flex-col items-center justify-center pt-6">
            <h3 className="text-sm font-semibold uppercase text-neutral">{t("filterChallenges")}</h3>
            <div className="flex flex-wrap items-center justify-center">
              <span
                onClick={toggleAllChallenges}
                className={`mx-1 mt-2 inline-flex items-center rounded-full bg-white px-3 py-1 text-sm font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 ${
                  allChallengesSelected() ? "bg-base-200 ring-base-300" : "bg-base-100 px-6 font-light text-opacity-40"
                } cursor-pointer`}
              >
                {t("allChallenges")}
                {allChallengesSelected() && <CheckIcon className={`ml-2 h-3 w-3`} />}
              </span>

              {challenges
                .filter(isMediaChallenge)
                .slice(0, challengesExpanded ? undefined : CHALLENGES_COLLAPSE_THRESHOLD)
                .map((challenge) => (
                  <span
                    key={challenge.id}
                    onClick={() => toggleChallenge(challenge)}
                    className={`mx-1 mt-2 inline-flex items-center rounded-full bg-white px-3 py-1 text-sm font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 ${
                      isChallengeSelected(challenge) ? "bg-base-200 ring-base-300" : "bg-base-100 px-6 text-opacity-40"
                    } cursor-pointer`}
                  >
                    <span>{challenge.title ?? challenge.title_en}</span>
                    {isChallengeSelected(challenge) && <CheckIcon className={`ml-2 h-3 w-3`} />}
                  </span>
                ))}

              {challenges.filter(isMediaChallenge).length > CHALLENGES_COLLAPSE_THRESHOLD && (
                <button
                  onClick={toggleChallengesExpanded}
                  className="mx-1 mt-2 inline-flex items-center px-3 py-1 text-xs font-medium underline"
                  title={challengesExpanded ? t("showLess") : t("showMore")}
                >
                  {challengesExpanded ? `${t("showLess")}` : `${t("showMore")}`}
                </button>
              )}
            </div>
          </div>
        )}

        <div
          className={`flex items-center justify-between ${
            multiTeam || (multiChallenge && hasChallenges) ? "pt-12" : ""
          } pb-4 lg:px-20`}
        >
          <div className="text-sm font-semibold uppercase text-neutral">
            {(multiTeam || (multiChallenge && hasChallenges)) && (
              <>
                {`${isEditMode ? t("editing") : t("showing")} ${galleryItems.length} ${t("from")} ${
                  allGalleryItems.length
                } ${t("totalPhotos")}`}
              </>
            )}
            <div>
              {isEditMode && selectedImages.size > 0 && (
                <span className="rounded-full bg-base-300 px-2 py-0.5 text-xs text-white">
                  {selectedImages.size} {t("selected")}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {allowDelete && (
              <button
                onClick={() => setIsEditMode(!isEditMode)}
                className="btn-ghost btn-sm btn m-0 flex items-center p-1"
                title={isEditMode ? t("viewMode") : t("editMode")}
              >
                {isEditMode ? <EyeIcon className="h-5 w-5" /> : <EditIcon className="h-5 w-5" />}
              </button>
            )}
            <button
              onClick={() => setIsDenseGrid(!isDenseGrid)}
              className="btn-ghost btn-sm btn m-0 p-1"
              title={isDenseGrid ? t("expandGrid") : t("compactGrid")}
            >
              {isDenseGrid ? <SmallGridIcon className="h-5 w-5" /> : <LargeGridIcon className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {isEditMode ? (
          // Edit view with pagination only
          <div>
            <div className={`pb-8 xl:px-32 ${gridClasses} justify-items-center`}>
              {galleryItems.map((item, index) => {
                // Extract needed properties from the gallery item
                const src = item.props["data-src"] || item.props["data-poster"];
                const title = item.props["data-sub-html"] ? item.props["data-sub-html"].replace(/<\/?h4>/g, "") : "";
                const imageUri = item.key;
                const isSelected = isImageSelected(imageUri);

                return (
                  <div
                    key={index}
                    className="relative flex aspect-square w-full cursor-pointer items-center justify-center"
                    onClick={() => toggleImageSelection(imageUri)}
                  >
                    <img
                      src={src || PLACEHOLDER_IMAGE}
                      alt={title}
                      className={`h-full w-full rounded-lg object-cover ${
                        isDenseGrid ? "aspect-square max-h-[16rem]" : "aspect-video max-h-[30rem]"
                      } ${isSelected ? "ring-2 ring-primary ring-offset-2" : ""}`}
                    />
                    <div className="absolute bottom-0 flex h-10 w-full flex-col items-center justify-center bg-gray-100 opacity-90">
                      <div className="text-sm font-semibold">
                        {title.split(" - ")[0]}
                        {item.props["data-video"] ? " 🍿🎞️" : null}
                      </div>
                      {title.includes(" - ") && (
                        <div className="overflow-hidden text-sm font-light">{title.split(" - ")[1]}</div>
                      )}
                    </div>

                    {/* Selection button in top left */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent double-toggling when clicking the button directly
                        toggleImageSelection(imageUri);
                      }}
                      className="absolute top-2 left-2 rounded-full bg-white p-2 text-base-300 shadow-md transition-all duration-200 hover:bg-gray-100"
                      title={isSelected ? t("deselect") : t("select")}
                    >
                      {isSelected ? <SelectedIcon className="h-5 w-5" /> : <UnselectedIcon className="h-5 w-5" />}
                    </button>

                    {/* Delete button in top right */}
                    {allowDelete && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteClick(imageUri);
                        }}
                        className="absolute top-2 right-2 rounded-full bg-red-500 p-2 text-white transition-opacity duration-200 hover:bg-red-600"
                        title={t("delete")}
                      >
                        <DeleteIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {renderPagination()}
          </div>
        ) : (
          // Gallery view with pagination only
          <div>
            <div className={`pb-8 xl:px-32 ${isLgContainerVisible ? "" : "hidden"}`}>
              <LightGallery
                elementClassNames={`${gridClasses} justify-items-center`}
                onInit={onInit}
                plugins={[lgVideo, lgZoom, lgThumbnail]}
                mode="lg-slide"
                speed={200}
                startClass=""
                startAnimationDuration={0}
                download={allowDownload}
                share={true}
                strings={{
                  download: t("downloadMedia"),
                  closeGallery: t("close"),
                  toggleMaximize: t("toggleMaximize"),
                  previousSlide: t("previous"),
                  nextSlide: t("next"),
                  playVideo: t("playVideo"),
                  mediaLoadingFailed: t("mediaLoadingFailed"),
                }}
                thumbnail={true}
                zoom={true}
                videoMaxSize={"40"}
                allowMediaOverlap={true}
                closable={true}
                loop={true}
                counter={true}
                progressBar={true}
                backdropDuration={0}
                hideControlOnEnd={false}
                zoomFromOrigin={false}
                licenseKey="your-license-key-here"
                mobileSettings={{
                  controls: true,
                  showCloseIcon: true,
                  download: allowDownload,
                }}
                addClass="lg-toolbar-visible"
                controls={true}
                showCloseIcon={true}
                autoplayControls={false}
              >
                {galleryItems}
              </LightGallery>
            </div>
            {renderPagination()}
          </div>
        )}
      </div>
      {multiTeam && renderDeleteSelectedButton()}
      {allowDelete && (
        <ConfirmModal
          show={showConfirmModal}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          messageCopy={
            bulkDeleteMode
              ? t("areYouSureYouWantToDeleteNImages", { count: selectedImages.size })
              : t("areYouSureYouWantToDeleteThisImage")
          }
          actionCopy={t("delete")}
        />
      )}
      {/* Mobile photo download modal */}
      {isMobile && mobileDownloadImageUri && (
        <MobilePhotoDownloadModal
          show={showMobileDownloadModal}
          onClose={closeMobileDownloadModal}
          imageUri={mobileDownloadImageUri}
          title={mobileDownloadTitle}
        />
      )}
    </>
  );
};
